
const DEFAULT_FILTER_DISTANCE = 50;

import CiAdvisorSearchTile from "@components/users/advisors/ci-advisor-search-tile.vue";
import { apiService } from "@services/api.service";
import { ViewportStoreModule } from "@stores/viewport.store";
import { debounce } from "throttle-debounce";
import { Constants } from "@app/constants";
import { Component, Watch, Ref } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import { BaseView } from "@components/base/view";
import { ValidationObserver } from "vee-validate";
import { ciTrackingService } from "@services/ci-tracking.service";
import { useLoader } from "@composables/loader";
import { NavigationGuardNext, Route } from "vue-router";
import { alternateService } from "@services/alternate.service";
import { LinkPropertyHref, MetaInfo } from "vue-meta";
import { storageService } from "@services/storage.service";
import { ROUTENAMES } from "@models/constants/routes";
import { ADS_INVENTORY } from "@models/constants/ads_inventory";
import { ADVISOR_SEARCH_PROMOTED_LOCATIONS } from "@models/constants/advisor_search_promoted_locations";

const ViewportStore = getModule(ViewportStoreModule);
const ADVISORS_PER_PAGE = 10;

@Component<any>({
  name: "advisor_search_view",
  components: {
    "ci-advisor-search-tile": CiAdvisorSearchTile,
    ValidationObserver,
  },
  metaInfo(): MetaInfo {
    return {
      title: this.serpSnippet("meta.title"),
      link: [{ vmid: "canonical", rel: "canonical", href: this.normalizedUrl }].concat(this.metaAlternates()),
      meta: [
        { vmid: "description", name: "description", content: this.serpSnippet("meta.description") },
        { vmid: "robots", name: "robots", content: this.robotsContent },
        { vmid: "og:title", property: "og:title", content: this.serpSnippet("meta.title") },
        { vmid: "og:url", property: "og:url", content: this.normalizedUrl },
        { vmid: "og:description", property: "og:description", content: this.serpSnippet("meta.description") },
        { vmid: "twitter:title", name: "twitter:title", content: this.serpSnippet("meta.title") },
        { vmid: "twitter:description", name: "twitter:description", content: this.serpSnippet("meta.description") },
      ],
    };
  },
})
export default class extends BaseView {
  @Ref("observer") observer!: InstanceType<typeof ValidationObserver>;

  public invalidLocationMessage: Nullable<string> = null;
  public advisors: UsersV2.Tile[] = [];
  public advisoryServices: UsersV2.AdvisoryServiceItem[] = [];
  public selectedAdvisoryId: Nullable<number | string> = null;
  public perimeter: number = DEFAULT_FILTER_DISTANCE;
  // raw location from params might be parameterized or not
  public location: Nullable<string> = null;
  public sortingOption: string = "score";
  public showNextPageButton: boolean = false;
  public page: number = 1;
  public count: number = 0;
  public searchCounter: number | string = "";
  public perimeterOptions: { label: number; value: number }[] = [10, 20, 50, 100, 150, 200, 250, 300, 400].map(
    (distance) => ({
      label: distance,
      value: distance,
    })
  );
  private loader = useLoader();
  private debounceSearch = debounce(Constants.defaultDelay, (vm: this): void => {
    vm.search();
  });

  public get promotedLocation(): Nullable<string> {
    const promotedLocationName = ADVISOR_SEARCH_PROMOTED_LOCATIONS.find(
      (element) => this.$parameterize(element) == this.locationParameterized
    );
    return promotedLocationName || null;
  }

  public get locationDisplay(): Nullable<string> {
    return this.promotedLocation || this.location;
  }

  public get headerTitle(): string {
    return this.serpSnippet(this.translationKeys.title);
  }

  public get subtitle(): string {
    return this.serpSnippet(this.translationKeys.subtitle);
  }

  public get searchResultsText(): string {
    return this.serpSnippet(this.translationKeys.searchResults);
  }

  public get adIds(): { header: string; skyscraper: string; sidebar_2nd: string } {
    return {
      header: ADS_INVENTORY.advisor_search_header_slot.dom_id,
      skyscraper: ADS_INVENTORY.advisor_search_sidebar_top.dom_id,
      sidebar_2nd: ADS_INVENTORY.advisor_search_sidebar_2nd.dom_id,
    };
  }

  public get sortingOptions(): Array<Record<string, any>> {
    return [
      {
        label: this.$t("ui.views.advisor_search.header.sorting.score"),
        value: "score",
      },
      {
        label: this.$t("ui.views.advisor_search.header.sorting.distance"),
        value: "distance",
        disabled: this.locationEmpty,
      },
    ];
  }

  public get selectedServiceTitle(): string | undefined {
    return this.advisoryServices.find((service) => service.id.toString() === this.selectedAdvisoryId?.toString())
      ?.title;
  }

  public get advisoryServicesOptions(): Array<Record<string, string | number>> {
    return this.advisoryServices?.map((value) => ({ label: value.title, value: value.id }));
  }

  public get mobileMode(): boolean {
    return ["xs", "sm"].includes(ViewportStore.currentViewport);
  }

  private get params(): Record<string, any> {
    return {
      location: this.location || null,
      advisory_service_id: this.selectedAdvisoryId || null,
      sorting_option: this.sortingOption || "score",
      page: this.page || 1,
      perimeter: this.perimeter,
    };
  }

  public get robotsContent(): string {
    // this.selectedService can only be null or a valid value, so it has no impact on the robots value
    if (this.locationEmpty || this.promotedLocation) {
      return "index, follow";
    }
    return "noindex, follow";
  }

  public get normalizedUrl(): string {
    const params: any = {
      service: this.selectedServiceTitle && this.$parameterize(this.selectedServiceTitle),
      location: this.locationParameterized || (this.selectedServiceTitle && "-"),
    };
    const path = this.$router.resolve({
      name: this.$routes.ADVISOR_SEARCH_INDEX,
      params: params,
    }).href;
    return window.location.origin + path;
  }

  private get selectedService(): any {
    return this.advisoryServices.find((el) => el.id === this.selectedAdvisoryId);
  }

  public get perimeterInputDisabled(): boolean {
    return this.locationEmpty;
  }

  public get translationKeys(): Record<string, string> {
    const sortedByDistance = this.sortingOption == "distance";

    return {
      title: sortedByDistance ? "header.title.sorted_by_distance" : "header.title",
      subtitle: sortedByDistance ? "header.subtitle.sorted_by_distance" : "header.subtitle",
      searchResults: sortedByDistance ? "search_results.sorted_by_distance" : "search_results",
    };
  }

  private get locationParameterized(): Nullable<string> {
    return this.$parameterize(this.location);
  }

  private get locationEmpty(): boolean {
    return this.location === null || this.location?.length === 0;
  }

  @Watch("params", { deep: true }) public async paramsModified(newVal: any, oldVal: any): Promise<void> {
    if (this.verifyChangedParam(oldVal, newVal)) {
      this.page = 1;
      if (this.selectedService !== undefined) storageService.set("selectedAdvisoryService", this.selectedService);
    }
    if (newVal.location === "") this.sortingOption = "score";
    this.invalidLocationMessage = null;
    this.debounceSearch(this);
  }

  public async loadNextAdvisors(): Promise<void> {
    this.page += 1;
  }

  public metaAlternates(): Array<LinkPropertyHref> {
    return this.locationEmpty ? alternateService.fromRoute(this.$route) : [];
  }

  public showButtonAction(): boolean {
    return (this.showNextPageButton = this.count > ADVISORS_PER_PAGE && this.count - this.advisors.length > 0);
  }

  public async mounted(): Promise<void> {
    ciTrackingService.trackView("AdvisorSearch");
  }

  public async beforeRouteUpdate(to: Route, _from: Route, next: NavigationGuardNext): Promise<void> {
    this.loadParamsFromUrl(to);
    next();
  }

  public async beforeRouteEnter(to: Route, _from: Route, next: NavigationGuardNext): Promise<void> {
    const loader = useLoader();
    loader.start();

    try {
      const response = await apiService.advisoryServices.getAll();
      next(async (vm: any) => {
        vm.advisoryServices = response.data.advisory_services;

        vm.loadParamsFromUrl(to);
        vm.debounceSearch(vm);
        vm.initialize();
      });
    } catch (error) {
      apiService.handleAPIError(error);
      next();
    } finally {
      loader.stop();
    }
  }

  public loadParamsFromUrl(route: Route): void {
    if (route.params.service?.length > 0) {
      this.selectedAdvisoryId =
        this.advisoryServices.find(
          (el: UsersV2.AdvisoryServiceItem) =>
            this.$parameterize(el.title) === this.$parameterize(decodeURI(route.params.service))
        )?.id || null;
    } else {
      this.selectedAdvisoryId = null;
    }

    if (!route.params.location || route.params.location === "-") this.location = null;
    else if (this.$parameterize(this.location) != route.params.location) {
      this.location = decodeURI(route.params.location);
    }
    this.sortingOption = (route.query.sortOrder || "score") as string;
    this.perimeter = Number(route.query.perimeter) || DEFAULT_FILTER_DISTANCE;
  }

  public resetFilters(): void {
    this.location = null;
    this.selectedAdvisoryId = null;
    this.sortingOption = "score";
    this.perimeter = DEFAULT_FILTER_DISTANCE;
  }

  public serpSnippet(element: string): string {
    if (this.location && !this.selectedAdvisoryId) {
      return this.$t(`ui.views.advisor_search.${element}.with_location`, {
        count: this.searchCounter,
        location: this.locationDisplay,
      }) as string;
    } else if (this.selectedAdvisoryId && !this.location) {
      return this.$t(`ui.views.advisor_search.${element}.with_service`, {
        count: this.searchCounter,
        service: this.selectedService?.title,
      }) as string;
    } else if (this.selectedAdvisoryId && this.location) {
      return this.$t(`ui.views.advisor_search.${element}.with_both`, {
        count: this.searchCounter,
        service: this.selectedService?.title,
        location: this.locationDisplay,
      }) as string;
    }

    return this.$t(`ui.views.advisor_search.${element}.main`) as string;
  }

  protected async beforeRouteLeave(to: Route, from: Route, next: NavigationGuardNext): Promise<void> {
    if (to.name !== ROUTENAMES.USERS_SHOW) {
      storageService.set("selectedAdvisoryService", null);
      next();
    } else {
      next();
    }
  }

  private verifyChangedParam(oldVal: any, newVal: any): boolean {
    return !!["location", "advisory_service_id", "sorting_option", "perimeter"]
      .map((el) => oldVal[el] !== newVal[el])
      .find((el) => el);
  }

  private async search(): Promise<void> {
    const result = await this.observer.validate();
    if (result) {
      const { params } = this;
      this.loader.start();
      apiService.advisors
        .search(params)
        .then((response: any) => {
          if (this.page > 1) {
            this.advisors.push(...response.data.advisors);
          } else {
            this.advisors = response.data.advisors;
          }
          this.count = response.data.meta.total_count;
          this.showButtonAction();
        })
        .catch((errors: any): void => {
          if (errors.response.status === 412) this.handleSearchErrors(errors.response.data.errors);
          else apiService.handleAPIError(errors);
        })
        .finally(() => {
          this.updateUrl();
          this.loader.stop();
          this.searchCounter = !this.selectedAdvisoryId && !this.location ? "" : this.advisors.length;
        });
    }
  }

  private handleSearchErrors(errors: Record<string, string>[]): void {
    if (errors.length > 0) {
      const error = JSON.parse(errors[0].msg);
      if (error.location) this.invalidLocationMessage = error.location;
    }
    this.advisors = [];
    this.observer.validate();
  }

  private trackSearchAdvisor(fromPath: string): void {
    try {
      ciTrackingService.track({
        event: {
          action: "search",
          value: null,
        },
        target: {
          name: "AdvisorSearch",
          identifier: "unused",
          url: location.href,
        },
        source: {
          name: "AdvisorSearch",
          identifier: location.origin + fromPath,
        },
      });
    } catch (err) {
      console.error(err);
    }
  }

  private async updateUrl(): Promise<void> {
    const params: Record<string, string> = {};
    if (this.selectedServiceTitle) params.service = this.$parameterize(this.selectedServiceTitle) as string;
    if (this.location) params.location = this.$parameterize(this.location) as string;
    if (this.selectedServiceTitle && !this.location) params.location = "-";

    const to = await this.$router.resolve({
      name: ROUTENAMES.ADVISOR_SEARCH_INDEX,
      params: params,
      query: {
        ...this.$route.query,
        sortBy: this.sortingOption,
        perimeter: this.perimeter.toString(),
      },
    });

    if (to.href !== this.$route.fullPath) {
      const sourceLocation = this.$route.fullPath;
      this.$router.push(to.href).then(() => this.trackSearchAdvisor(sourceLocation));
    }
  }
}
